/**
 * Accessibility Utilities and Hooks
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect if user prefers reduced motion
 * @returns boolean indicating if reduced motion is preferred
 */
export function usePrefersReducedMotion(): boolean {
  const mediaQuery = useRef<MediaQueryList | null>(null);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    mediaQuery.current = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mediaQuery.current.matches;

    const handleChange = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };

    mediaQuery.current.addEventListener('change', handleChange);

    return () => {
      mediaQuery.current?.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion.current;
}

/**
 * Hook to detect if user prefers high contrast
 * @returns boolean indicating if high contrast is preferred
 */
export function usePrefersHighContrast(): boolean {
  const mediaQuery = useRef<MediaQueryList | null>(null);
  const prefersHighContrast = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    mediaQuery.current = window.matchMedia('(prefers-contrast: high)');
    prefersHighContrast.current = mediaQuery.current.matches;

    const handleChange = (e: MediaQueryListEvent) => {
      prefersHighContrast.current = e.matches;
    };

    mediaQuery.current.addEventListener('change', handleChange);

    return () => {
      mediaQuery.current?.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersHighContrast.current;
}

/**
 * Generate a unique ID for accessibility purposes
 * Useful for linking labels to inputs, or aria-describedby
 *
 * @param prefix - Prefix for the ID
 * @returns Unique ID string
 */
/**
 * Hook to trap focus within a container element (e.g., modals/dialogs).
 * When active, Tab/Shift+Tab cycle through focusable elements inside the container.
 * Pressing Escape calls the onClose callback.
 *
 * @param isActive - Whether the focus trap is active
 * @param onClose - Callback when Escape is pressed
 * @returns ref to attach to the container element
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  isActive: boolean,
  onClose?: () => void
) {
  const containerRef = useRef<T>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    const selector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector));
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // Save the currently focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus the first focusable element inside the container
    const timer = setTimeout(() => {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.preventDefault();
        onCloseRef.current();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the previously focused element
      previousFocusRef.current?.focus();
    };
  }, [isActive, getFocusableElements]);

  return containerRef;
}

export function useUniqueId(prefix: string = 'a11y'): string {
  const idRef = useRef<string>('');

  if (!idRef.current) {
    idRef.current = `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }

  return idRef.current;
}
